<template>
  <div
    v-if="liquid?.enableColourSwatch && hasRelatedProducts || showColors"
    class="colour-swatch"
    :class="location"
  >
    <ul class="colour-swatch__swatch-list">

      <li
        v-for="(item, index) of coloursList"
        :key="index"
        class="colour-swatch__swatch-item"
      >
        <button
          class="colour-swatch__swatch-button"
          :class="
            [
              `${getOptionColour(item)}`,
              {
                [$class('disabled')]: !item?.available,
                [$class('active')]: item.handle === product.handle
              }
            ]
          "
          type="button"
          :style="getColourStyle(item)"
          @click="isActive(item?.handle)"
        >
          <span
            class="colour-swatch__colour-image"
            :style="getColourImageStyle(item)"
          />
        </button>
      </li>
    </ul>
  </div>
</template>

<script>

/**
 * Vue: Colour swatch (colour-swatch)
 * -----------------------------------------------------------------------------
 * Colour swatch.
 *
 * @param {Object} liquid - Liquid object.
 * @param {Boolean|Object} product - Product object.
 * @param {Array} colours - Array of related products.
 */
import { mapState } from 'vuex'

export default {
  name: 'ColourSwatch',

  props: {
    liquid: {
      type: Object,
      default: () => ({
        productId: 0,
        sectionId: '',
        title: '',
        enableColourSwatch: false,
      }),
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    showColors: {
      type: Boolean,
      default: false,
    },
    colours: {
      type: Array,
      default: () => [],
      required: true,
    },
    showAllColours: {
      type: Boolean,
      default: false,
    },
    location: {
      type: String,
      default: '',
    },
  },

  emits: {
    'update-colour-swatch': (payload) => typeof payload === 'string',
  },

  data() {
    return {
      error: '',
      state: 'ready',
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Has related product.
     * @returns {Boolean}
     */
    hasRelatedProducts() {
      if (!this.colours) {
        return false
      }

      return this.colours?.length > 0
    },

    coloursList() {
      if (this.showAllColours && !this.isMobile) {
        return this.colours
      }

      return this.colours.slice(0, 7)
    }
  },

  methods: {

    /**
     * Emit update to colour swatch event.
     * @param {String} handle - product handle.
     */
    isActive(handle) {
      this.$emit('update-colour-swatch', handle)
    },

    /**
     * Get option colour.
     * @param {Object} item - related product.
     * @returns {String}
     */
    getOptionColour(item) {
      const colorVariant = item?.options?.find((option) => {
        const optionName = option.name?.toLowerCase()
        return optionName === 'colour' || optionName === 'color'
      })

      const colorVariantValues = colorVariant.values[0].toLowerCase().replaceAll(' ', '-')

      return colorVariant ? colorVariantValues : ''
    },

    /**
     * Get colour style.
     * - Data is from metaobject which is filtered and returns a new array if
     * 'colourHandle' and 'optionColour' matches
     * @param {Object} item - related product.
     * @returns {Object}
     */
    getColourStyle(item) {
      const filteredData = cnvs.colours.filter((data) => {
        const colourHandle = data.handle

        return colourHandle === this.getOptionColour(item)
      })

      const colourPrimary = filteredData[0]?.colour_primary
      const colourSecondary = filteredData[0]?.colour_secondary

      if (colourPrimary && colourSecondary) {
        return {
          background: `-webkit-linear-gradient(left, ${colourPrimary} 50%, ${colourSecondary} 50%)`,
        }
      }

      if (colourPrimary) {
        return {
          background: colourPrimary,
        }
      }

      return false
    },

    /**
     * Get colour image style.
     * - Data is from metaobject which is filtered and returns a new array if
     * 'colourHandle' and 'optionColour' matches
     * @param {Object} item - related product.
     * @returns {Object}
     */
    getColourImageStyle(item) {
      const filteredData = cnvs.colours.filter((data) => {
        const colourHandle = data.handle

        return colourHandle === this.getOptionColour(item)
      })

      const image = filteredData[0]?.image

      if (image) {
        return {
          backgroundImage: `url('${image}')`,
        }
      }

      return false
    },
  },
}
</script>

<style lang="scss">
@import './colour-swatch';
</style>
